import axios from 'axios';
import { FIREBASE_CONFIG } from 'configs/firebase.config';

export type GeolocationFailureReason = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

// Lets callers branch on *why* geolocation failed instead of a numeric .code.
export class GeolocationError extends Error {
  readonly reason: GeolocationFailureReason;
  constructor(reason: GeolocationFailureReason, message: string) {
    super(message);
    this.name = 'GeolocationError';
    this.reason = reason;
  }
}

// GeolocationPositionError.code: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT.
const REASON_BY_POSITION_ERROR_CODE: Record<number, GeolocationFailureReason> = {
  1: 'denied',
  2: 'unavailable',
  3: 'timeout',
};

export const getUserCoordinates = (
  options?: PositionOptions,
): Promise<{ latitude: number; longitude: number }> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      // `?.` here would silently skip both callbacks and hang this Promise forever.
      reject(new GeolocationError('unsupported', 'Geolocation is not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (posistion) => {
        try {
          resolve(posistion.coords);
        } catch (err) {
          reject(err);
        }
      },
      (err) => {
        reject(
          new GeolocationError(
            REASON_BY_POSITION_ERROR_CODE[err.code] ?? 'unavailable',
            err.message,
          ),
        );
      },
      { enableHighAccuracy: true, ...options },
    );
  });
};

interface Coordinates {
  lat: number;
  long: number;
}
export interface AddressComponent {
  long_name: string;
  types: string[];
}
interface GoogleMapsGeocodeResult {
  address_components: AddressComponent[];
  formatted_address: string;
  geometry: {
    location: { lat: number; lng: number };
    location_type: string;
    viewport: { northeast: Coordinates; southeast: Coordinates };
  };
  place_id: string;
  plus_code: { compound_code: string; global_code: string };
  types: string[];
}

export const getUserLocationDetails = async (): Promise<{
  country: string;
  city: string;
  latitude: number;
  longitude: number;
}> => {
  let country = '';
  let city = '';
  let latitude = 0;
  let longitude = 0;
  try {
    const coordinates = await getUserCoordinates();
    latitude = coordinates.latitude;
    longitude = coordinates.longitude;
    const response =
      await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${FIREBASE_CONFIG?.apiKey}
    `);
    const result: GoogleMapsGeocodeResult = response.data?.results?.[0];
    if (result) {
      result.address_components.forEach((addressComponent: AddressComponent) => {
        if (addressComponent.types.includes('country')) {
          country = addressComponent.long_name;
        } else if (addressComponent.types.includes('locality')) {
          city = addressComponent.long_name;
        }
      });
    }
    return { country, city, latitude, longitude };
  } catch (err) {
    if (!(err instanceof GeolocationError && err.reason === 'unsupported')) {
      console.error(err);
    }
    return { country, city, latitude, longitude };
  }
};

function degreesToRadians(degrees: number) {
  return (degrees * Math.PI) / 180;
}

export function distanceInMBetweenEarthCoordinates({
  lat1,
  lon1,
  lat2,
  lon2,
}: {
  lat1: number;
  lon1: number;
  lat2: number;
  lon2: number;
}) {
  const earthRadiusKm = 6371;

  const dLat = degreesToRadians(lat2 - lat1);
  const dLon = degreesToRadians(lon2 - lon1);

  lat1 = degreesToRadians(lat1);
  lat2 = degreesToRadians(lat2);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(earthRadiusKm * c * 1000);
}
